import { Injectable } from "@angular/core";
import {
	HomePageGameCollection,
	HomePageWidget,
	RegistrationStart,
	SponsorshipBannerConfig,
	SponsorshipBannerType,
} from "@obg/common.config";
import { HomeConfigService } from "@obg/core.home";
import { MarketConfigService } from "@obg/ngx.core";
import { TelemetryService } from "@obg/ngx.global.evo";

import { GAMING_QUICK_LINKS, PREVIEW_SECTION_LABEL, PreviewSectionType } from "./home-wrapper.const";

@Injectable({
	providedIn: "root",
})
export class HomeWrapperService {
	constructor(
		private config: HomeConfigService,
		private telemetryService: TelemetryService,
		private marketConfig: MarketConfigService
	) {}

	getWidgets(orderArrayFromParentCollection?: string[]): HomePageWidget[] {
		const { defaultMarket, marketSpecific } = this.config.getComponentOrder();
		const marketCode = this.marketConfig.getLanguageCode();
		const customItems = this.getGameCollectionNames();
		let widgets = defaultMarket || [];

		if (customItems && this.isWebComponentEnabled(PreviewSectionType.GamingPreviewSection)) {
			widgets = this.addCustomItemsToWidgets(
				widgets,
				customItems,
				orderArrayFromParentCollection ?? [],
				GAMING_QUICK_LINKS.CasinoQuickLinks,
				GAMING_QUICK_LINKS.LiveCasinoQuickLinks
			);
		}

		if (marketSpecific && marketSpecific[marketCode]) {
			const mergedArray = [...widgets, ...marketSpecific[marketCode]];
			widgets = [
				...mergedArray
					.reduce((map, obj) => {
						const filterBy = obj?.documentKey ?? obj.label;
						return map.set(filterBy, obj);
					}, new Map())
					.values(),
			];
		}

		return widgets
			.filter((obj) => obj?.isEnabled)
			.sort((a: HomePageWidget, b: HomePageWidget) => a.order - b.order);
	}

	isSponsorshipBannerEnabled(bannerType: SponsorshipBannerType): boolean {
		const sponsorshipBanner = this.getSponsorshipBanner(bannerType);

		if (!sponsorshipBanner || !sponsorshipBanner.isEnabled) {
			return false;
		}

		const specificMarket = this.config.getSponsorshipBannerSpecificMarket(sponsorshipBanner);

		if (!specificMarket.length) {
			return true;
		}

		return !!specificMarket.find(
			(market) => market.toLowerCase() === this.marketConfig.getLanguageCode().toLowerCase()
		);
	}

	isSponsorshipCtaEnabled(bannerType: SponsorshipBannerType): boolean {
		const sponsorshipBanner = this.getSponsorshipBanner(bannerType);
		return sponsorshipBanner?.isEnabledCTA ?? false;
	}

	getSponsorshipBannersState(): SponsorshipBannerConfig[] | [] {
		return this.config.getSponsorshipBannersState().length > 0 ? this.config.getSponsorshipBannersState() : [];
	}

	getSponsorshipBanner(bannerType: SponsorshipBannerType): SponsorshipBannerConfig | undefined {
		const configBanner: SponsorshipBannerConfig[] = this.getSponsorshipBannersState();
		return configBanner.find((banner) => banner.bannerType === bannerType);
	}

	isMainBannerHidden(): boolean {
		const { isHidden, specificMarket } = this.config.getMainBanner();

		const isSpecificMarketExist = (specificMarkets: string[]) =>
			specificMarkets.some(
				(market) => market.toLowerCase() === this.marketConfig.getLanguageCode().toLowerCase()
			);

		if (!specificMarket?.length || isSpecificMarketExist(specificMarket)) {
			return isHidden;
		}

		return false;
	}

	isRegistrationBannerHidden() {
		return this.config.getHideRegistrationBanner();
	}

	getRegistrationStart(): RegistrationStart | undefined {
		return this.config.getRegistrationStart();
	}

	isWebComponentEnabled(componentName: string): boolean {
		return this.config.getEnabledWebComponents().includes(componentName);
	}

	getGameCollectionNames(): Record<string, HomePageGameCollection> | undefined {
		return this.config.getGameCollectionNames();
	}

	getParentGameCollectionName(): string | undefined {
		return this.config.getGameParentCollectionName();
	}

	filterContent<T extends { documentKey?: string }>(contents: T[], ...gamingQuickLinks: GAMING_QUICK_LINKS[]): T[] {
		return contents.filter((content) => this.getCondition(content.documentKey!, gamingQuickLinks, false));
	}

	isCarouselBannerEnabled(): boolean {
		return this.config.isCarouselBannerEnabled();
	}

	private addCustomItemsToWidgets(
		widgets: HomePageWidget[],
		customItems: Record<string, HomePageGameCollection>,
		orderArrayFromParentCollection: string[],
		...gamingQuickLinks: GAMING_QUICK_LINKS[]
	): HomePageWidget[] {
		const oldGamingWidget = widgets.find((widget) => this.getCondition(widget.documentKey!, gamingQuickLinks));

		if (!oldGamingWidget) {
			return widgets;
		}

		const filteredWidgets = this.filterContent(widgets, ...gamingQuickLinks) as HomePageWidget[];
		const customItemValues: HomePageWidget[] = this.createHomePageWidgetArray(
			customItems,
			oldGamingWidget.order,
			orderArrayFromParentCollection
		);

		let increaseNumber = 0;

		filteredWidgets.forEach((widget) => {
			if (widget.order > oldGamingWidget.order) {
				widget.order = oldGamingWidget.order + customItemValues.length + increaseNumber;
				increaseNumber++;
			}
		});

		filteredWidgets.push(...customItemValues);

		return filteredWidgets;
	}

	private getCondition(documentKey: string, gamingQuickLinks: GAMING_QUICK_LINKS[], union = true): boolean {
		return union
			? gamingQuickLinks.includes(documentKey as GAMING_QUICK_LINKS)
			: !gamingQuickLinks.includes(documentKey as GAMING_QUICK_LINKS);
	}

	private createHomePageWidgetArray(
		customItems: Record<string, HomePageGameCollection>,
		orderNumber: number,
		orderArrayFromParentCollection: string[]
	) {
		const customWidgetCollection: HomePageWidget[] = [];

		Object.keys(customItems).forEach((key, index) => {
			const newIndex = orderArrayFromParentCollection?.length
				? orderArrayFromParentCollection.findIndex((name) => name === key) ?? index
				: index;

			customWidgetCollection.push({
				order: orderNumber + newIndex,
				isEnabled: true,
				label: PREVIEW_SECTION_LABEL,
				documentKey: key,
				widgetConfig: {
					itemPreview:
						customItems[key].oneRowThreshold && customItems[key].twoRowThreshold
							? {
									oneRowThreshold: customItems[key].oneRowThreshold!,
									twoRowThreshold: customItems[key].twoRowThreshold!,
								}
							: undefined,
				},
			});
		});

		return customWidgetCollection;
	}

	showMainBannerForLoggedOutUser(): boolean {
		return this.config.getShowMainBannerForLoggedOutUser();
	}

	async isHomePageFabricVariantActive(): Promise<boolean> {
		const configEnabled = this.isHomePageFabricEnabled();
		const variant = await this.telemetryService.getExperimentVariant("fns", 2000);

		if (!configEnabled) {
			return false;
		}

		return configEnabled || variant === "1";
	}

	private isHomePageFabricEnabled(): boolean {
		return this.config.getIsHomePageFabricEnabled();
	}
}
